import { useEffect, useRef, useState } from "react";
import { findActiveVersionId } from "../utils/versionHistory.utils";
import { VersionHistoryGroup } from "../types/versionHistory.types";

/**
 * Only the active version is expanded by default (decision 2), and that seeding must survive a
 * background refetch without fighting a user's own expand/collapse choices. A refetch that
 * returns the same page with the same active version produces an identical `seedKey`, so the
 * effect is a no-op and every toggle the user made stays put. Real transitions -- page change,
 * sales-unit change, or an Activate that flips isDefault -- change the key, so the newly active
 * row opens.
 */
export function useVersionRowExpansion(params: { versions: VersionHistoryGroup[]; seedScope: string }) {
  const [expandedVersionIds, setExpandedVersionIds] = useState<Record<string, boolean>>({});
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [selectedRecordIdByVersion, setSelectedRecordIdByVersion] = useState<Record<string, string>>({});
  const seededKeyRef = useRef<string | null>(null);

  const activeVersionId = findActiveVersionId(params.versions);
  const seedKey = `${params.seedScope}|${activeVersionId ?? ""}`;

  useEffect(() => {
    if (!activeVersionId || seededKeyRef.current === seedKey) return;
    seededKeyRef.current = seedKey;
    setExpandedVersionIds({ [activeVersionId]: true });
    setSelectedVersionId(activeVersionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedKey, activeVersionId]);

  const toggleVersion = (versionId: string) => {
    setExpandedVersionIds(prev => ({ ...prev, [versionId]: !prev[versionId] }));
    setSelectedVersionId(versionId);
  };

  /** Set-true rather than toggle: used when opening a row to reveal something inside it
   * (the details editor), where a toggle would close an already-open row. */
  const expandVersion = (versionId: string) => {
    setExpandedVersionIds(prev => ({ ...prev, [versionId]: true }));
    setSelectedVersionId(versionId);
  };

  const selectRecord = (versionId: string, recordId: string) => {
    setSelectedRecordIdByVersion(prev => ({ ...prev, [versionId]: recordId }));
    setSelectedVersionId(versionId);
  };

  return {
    expandedVersionIds,
    selectedVersionId,
    selectedRecordIdByVersion,
    toggleVersion,
    expandVersion,
    selectRecord
  };
}
